import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, set } from 'firebase/database';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { Container, Card, Button, Form, Alert, Modal, Badge, Table, Dropdown } from 'react-bootstrap';
import { Plus, Calendar, Settings, Clock, AlertTriangle, CheckCircle, Check, BarChart3, MoreVertical, Trash2, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getAuth } from 'firebase/auth';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { 
    BuildingCheckWithTemplate, 
    CheckCompletion, 
    formatDate, 
    formatDaysUntilDue, 
    getDaysUntilDue, 
    calculateNextDueDate,
    loadAllBuildingChecks,
    updateActiveCheckWithLastCompletion
} from './buildingChecksUtils';
import './BuildingChecksPage.css';
import { Helmet } from 'react-helmet-async';

const AllBuildingChecksPage: React.FC = () => {
    const navigate = useNavigate();
    const auth = getAuth();
    const user = auth.currentUser;
    const { buildings } = useBuildingsData();
    const [allChecks, setAllChecks] = useState<BuildingCheckWithTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    
    // Completion modal state
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [checkToComplete, setCheckToComplete] = useState<BuildingCheckWithTemplate | null>(null);
    const [saving, setSaving] = useState(false);
    const [modalAlert, setModalAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    const [completionForm, setCompletionForm] = useState({
        completedAt: '',
        measurementValue: '',
        notes: ''
    });

    // Statistics modal state
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [checkForStats, setCheckForStats] = useState<BuildingCheckWithTemplate | null>(null);
    const [checkResults, setCheckResults] = useState<CheckCompletion[]>([]);
    
    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkToDelete, setCheckToDelete] = useState<BuildingCheckWithTemplate | null>(null);

    const db = getDatabase();

    // Load all building checks
    useEffect(() => {
        if (buildings && buildings.length > 0) {
            loadAllChecks();
        }
    }, [buildings]);

    const loadAllChecks = async () => {
        if (!buildings || buildings.length === 0) {
            setAllChecks([]);
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            const buildingsForChecks = buildings.map(building => ({
                key: building.key,
                name: building.name || 'Namnlös byggnad'
            }));
            const checks = await loadAllBuildingChecks(buildingsForChecks);
            setAllChecks(checks);
        } catch (error) {
            console.error('Error loading all checks:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av kontroller' });
        } finally {
            setLoading(false);
        }
    };


    const openCompletionModal = (check: BuildingCheckWithTemplate) => {
        setCheckToComplete(check);
        setCompletionForm({
            completedAt: new Date().toISOString().split('T')[0],
            measurementValue: '',
            notes: ''
        });
        setModalAlert(null); // Clear any previous modal alerts
        setShowCompletionModal(true);
    };

    const openStatsModal = async (check: BuildingCheckWithTemplate) => {
        if (!check.buildingId) return;
        
        setCheckForStats(check);
        setShowStatsModal(true);
        await loadCheckResults(check.buildingId, check.templateId);
    };

    const loadCheckResults = async (buildingId: string, templateId: string) => {
        try {
            const resultsRef = ref(db, `buildingChecks/results/${buildingId}/${templateId}`);
            const resultsSnapshot = await get(resultsRef);
            
            if (resultsSnapshot.exists()) {
                const resultsData = resultsSnapshot.val();
                const results: CheckCompletion[] = [];
                
                for (const [date, completionData] of Object.entries(resultsData)) {
                    results.push({
                        ...(completionData as CheckCompletion),
                        completedAt: date
                    });
                }
                
                results.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
                setCheckResults(results);
            } else {
                setCheckResults([]);
            }
        } catch (error) {
            console.error('Error loading check results:', error);
            setCheckResults([]);
        }
    };

    const handleCompleteCheck = async () => {
        if (!checkToComplete || !user || !checkToComplete.buildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        // Validate measurement if required
        if (checkToComplete.template.takesMeasurement && (!completionForm.measurementValue || completionForm.measurementValue.trim() === '')) {
            setModalAlert({ type: 'danger', message: 'Mätvärde krävs för denna kontroll. Vänligen ange ett värde.' });
            return;
        }

        try {
            setSaving(true);
            const now = new Date().toISOString();
            
            const nextDueDate = calculateNextDueDate(
                checkToComplete.template, 
                completionForm.completedAt, 
                checkToComplete.nextDueDate
            );

            const completionData: any = {
                templateId: checkToComplete.templateId,
                completedBy: user.uid,
                completedAt: completionForm.completedAt,
                submittedAt: now,
                actualDueDate: checkToComplete.nextDueDate,
                nextDueDate: nextDueDate
            };

            if (completionForm.notes && completionForm.notes.trim()) {
                completionData.notes = completionForm.notes.trim();
            }

            if (checkToComplete.template.takesMeasurement && completionForm.measurementValue) {
                const measurementValue = parseFloat(completionForm.measurementValue);
                const range = checkToComplete.template.takesMeasurement.acceptableRange;
                
                let withinRange = true;
                if (range) {
                    if (range.min !== undefined && measurementValue < range.min) withinRange = false;
                    if (range.max !== undefined && measurementValue > range.max) withinRange = false;
                }

                completionData.measurement = {
                    value: measurementValue,
                    unit: checkToComplete.template.takesMeasurement.metric,
                    withinRange: withinRange
                };
            }

            // Save completion to results
            const completionRef = ref(db, `buildingChecks/results/${checkToComplete.buildingId}/${checkToComplete.templateId}/${completionForm.completedAt}`);
            await set(completionRef, completionData);

            // Update active check with last completion data
            const measurement = completionData.measurement ? {
                value: completionData.measurement.value,
                unit: completionData.measurement.unit,
                withinRange: completionData.measurement.withinRange
            } : undefined;

            await updateActiveCheckWithLastCompletion(
                checkToComplete.buildingId,
                checkToComplete.templateId,
                completionForm.completedAt,
                nextDueDate,
                measurement
            );

            setAlert({ type: 'success', message: 'Kontroll markerad som klar!' });
            setShowCompletionModal(false);
            setCheckToComplete(null);
            setCompletionForm({ completedAt: '', measurementValue: '', notes: '' });
            loadAllChecks();
        } catch (error) {
            console.error('Error completing check:', error);
            setAlert({ type: 'danger', message: 'Fel vid markering av kontroll som klar' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCheck = async () => {
        if (!checkToDelete || !checkToDelete.buildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        try {
            setSaving(true);
            
            const activeCheckRef = ref(db, `buildingChecks/active/${checkToDelete.buildingId}/${checkToDelete.templateId}`);
            await set(activeCheckRef, null);

            setAlert({ type: 'success', message: 'Kontroll borttagen framgångsrikt!' });
            setShowDeleteModal(false);
            setCheckToDelete(null);
            loadAllChecks();
        } catch (error) {
            console.error('Error deleting check:', error);
            setAlert({ type: 'danger', message: 'Fel vid borttagning av kontroll' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="mt-2">Laddar kontroller...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <Helmet>
                <title>Alla kommande kontroller - Molnfastigheter</title>
            </Helmet>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Alla kommande kontroller</h2>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/settings?tab=building-checks')}
                    >
                        <Settings size={16} className="me-1" />
                        Hantera kontrollmallar
                    </Button>
                </div>
            </div>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {allChecks.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <Clock size={48} className="text-muted mb-3" />
                        <h5>Inga kontroller hittades</h5>
                        <p className="text-muted">
                            Det finns inga aktiva kontroller i någon byggnad.
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <Card className="shadow-sm" style={{ borderRadius: '0.5rem' }}>
                    <Card.Body className="p-4 bg-white" style={{ borderRadius: '0.5rem' }}>
                        <Table responsive striped hover>
                            <thead>
                                <tr>
                                    <th>Byggnad</th>
                                    <th>Kontroll</th>
                                    <th>Förfallodatum</th>
                                    <th>Senast genomförd</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allChecks.map((check) => {
                                    const daysText = formatDaysUntilDue(check.nextDueDate);
                                    const isLastToday = check.lastCompletedDate === new Date().toISOString().split('T')[0];
                                    
                                    return (
                                        <tr key={`${check.buildingId}-${check.templateId}`}>
                                            <td>
                                                <strong>{check.buildingName}</strong>
                                            </td>
                                            <td>
                                                <strong>{check.template.name}</strong>
                                                {check.template.estimatedTime > 0 && (
                                                    <div className="small text-muted">
                                                        {check.template.estimatedTime} min
                                                    </div>
                                                )}
                                                {check.template.takesMeasurement && (
                                                    <div className="small text-muted mt-1">
                                                        <strong>Mätvärde:</strong> {check.template.takesMeasurement.metric}
                                                        {check.template.takesMeasurement.acceptableRange && (
                                                            <span>
                                                                {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                                    <span> ({check.template.takesMeasurement.acceptableRange.min} - {check.template.takesMeasurement.acceptableRange.max})</span>
                                                                )}
                                                                {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max === undefined && (
                                                                    <span> (min: {check.template.takesMeasurement.acceptableRange.min})</span>
                                                                )}
                                                                {check.template.takesMeasurement.acceptableRange.min === undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                                    <span> (max: {check.template.takesMeasurement.acceptableRange.max})</span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div>{formatDate(check.nextDueDate)}</div>
                                                <div className={`small ${
                                                    getDaysUntilDue(check.nextDueDate) < 0 ? 'text-danger' : 
                                                    getDaysUntilDue(check.nextDueDate) === 0 ? 'text-warning' : 
                                                    getDaysUntilDue(check.nextDueDate) <= check.template.advanceNotice ? 'text-danger' : 
                                                    'text-muted'
                                                }`}>
                                                    {daysText}
                                                </div>
                                            </td>
                                            <td>
                                                {check.lastCompletedDate ? (
                                                    <div>
                                                        <div className="small d-flex align-items-center">
                                                            {formatDate(check.lastCompletedDate)}
                                                            {check.lastCompletedDate === new Date().toISOString().split('T')[0] && (
                                                                <Check size={16} className="text-success ms-1" />
                                                            )}
                                                        </div>
                                                        {check.template.takesMeasurement && check.lastMeasurement && (
                                                            <div className="small text-muted">
                                                                {check.lastMeasurement.value} {check.lastMeasurement.unit}
                                                                {!check.lastMeasurement.withinRange && (
                                                                    <span className="text-danger ms-1">⚠</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">Aldrig genomförd</span>
                                                )}
                                            </td>
                                            <td>
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${check.buildingId}-${check.templateId}`}>
                                                        <MoreVertical size={16} />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => openCompletionModal(check)}>
                                                            <CheckCircle size={16} className="me-2" />
                                                            Markera som utförd
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => openStatsModal(check)}>
                                                            <BarChart3 size={16} className="me-2" />
                                                            Visa statistik
                                                        </Dropdown.Item>
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item 
                                                            onClick={() => {
                                                                setCheckToDelete(check);
                                                                setShowDeleteModal(true);
                                                            }}
                                                            className="text-danger"
                                                        >
                                                            <Trash2 size={16} className="me-2" />
                                                            Radera
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* Completion Modal */}
            <Modal show={showCompletionModal} onHide={() => setShowCompletionModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Markera kontroll som klar</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAlert && (
                        <Alert variant={modalAlert.type} className="mb-3">
                            {modalAlert.message}
                        </Alert>
                    )}
                    {checkToComplete && (
                        <>
                            <div className="mb-3">
                                <h6>Kontrollinformation</h6>
                                <p><strong>Byggnad:</strong> {checkToComplete.buildingName}</p>
                                <p><strong>Namn:</strong> {checkToComplete.template.name}</p>
                                <p><strong>Beskrivning:</strong> {checkToComplete.template.description}</p>
                                <p><strong>Förfallodatum:</strong> {formatDate(checkToComplete.nextDueDate)}</p>
                            </div>

                            {/* Instructional Attachments */}
                            {checkToComplete.template.instructionalAttachments && Object.keys(checkToComplete.template.instructionalAttachments).length > 0 && (
                                <div className="mb-3">
                                    <h6>Instruktionsbilagor</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {Object.entries(checkToComplete.template.instructionalAttachments).map(([dbKey, attachment]) => (
                                            <Button
                                                key={dbKey}
                                                variant="outline-primary"
                                                size="sm"
                                                className="d-flex align-items-center"
                                                onClick={() => {
                                                    // Create download URL from storage path
                                                    const storage = getStorage();
                                                    const fileRef = storageRef(storage, attachment.storagePath);
                                                    getDownloadURL(fileRef).then((url) => {
                                                        // Open in new tab
                                                        window.open(url, '_blank');
                                                    }).catch((error) => {
                                                        console.error('Error getting download URL:', error);
                                                        window.alert('Fel vid hämtning av fil');
                                                    });
                                                }}
                                            >
                                                <FileText size={14} className="me-1" />
                                                {attachment.key}
                                            </Button>
                                        ))}
                                    </div>
                                    <small className="text-muted">Klicka på en bilaga för att öppna den i en ny flik</small>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>När genomfördes kontrollen? *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={completionForm.completedAt}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, completedAt: e.target.value }))}
                                    required
                                />
                                {completionForm.completedAt && checkToComplete && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Nästa förfallodatum beräknat på valt datum: {calculateNextDueDate(
                                                checkToComplete.template, 
                                                completionForm.completedAt, 
                                                checkToComplete.nextDueDate
                                            )}
                                        </small>
                                    </div>
                                )}
                            </Form.Group>

                            {checkToComplete.template.takesMeasurement && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Mätvärde ({checkToComplete.template.takesMeasurement.metric}) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        value={completionForm.measurementValue}
                                        onChange={(e) => setCompletionForm(prev => ({ ...prev, measurementValue: e.target.value }))}
                                        placeholder="Ange mätvärde"
                                        required
                                    />
                                    {checkToComplete.template.takesMeasurement.acceptableRange && (
                                        <Form.Text className="text-muted">
                                            Acceptabelt intervall: 
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> {checkToComplete.template.takesMeasurement.acceptableRange.min} - {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max === undefined && (
                                                <span> Min: {checkToComplete.template.takesMeasurement.acceptableRange.min}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min === undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> Max: {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Anteckningar</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={completionForm.notes}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Valfria anteckningar om genomförandet..."
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompletionModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCompleteCheck}
                        disabled={!completionForm.completedAt || saving}
                    >
                        {saving ? 'Sparar...' : 'Markera som klar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Statistics Modal */}
            <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <BarChart3 size={20} className="me-2" />
                        Statistik och loggar - {checkForStats?.template.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkForStats && (
                        <>
                            {checkResults.length === 0 ? (
                                <div className="text-center py-4">
                                    <BarChart3 size={48} className="text-muted mb-3" />
                                    <h5>Inga resultat ännu</h5>
                                    <p className="text-muted">
                                        Denna kontroll har inte genomförts ännu. Resultat och statistik kommer att visas här efter första genomförandet.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Chart for measurements */}
                                    {checkForStats.template.takesMeasurement && checkResults.some(r => r.measurement) && (
                                        <div className="mb-4">
                                            <h6>Mätvärden över tid</h6>
                                            <div style={{ height: '300px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={checkResults
                                                            .filter(r => r.measurement)
                                                            .map(r => ({
                                                                date: new Date(r.completedAt).toLocaleDateString('sv-SE'),
                                                                value: r.measurement?.value || 0,
                                                                withinRange: r.measurement?.withinRange || false
                                                            }))
                                                            .reverse()
                                                        }
                                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis 
                                                            dataKey="date" 
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={80}
                                                        />
                                                        <YAxis 
                                                            label={{ value: checkForStats.template.takesMeasurement?.metric || '', angle: -90, position: 'insideLeft' }}
                                                        />
                                                        <Tooltip 
                                                            formatter={(value: any, name: string) => [
                                                                `${value} ${checkForStats.template.takesMeasurement?.metric || ''}`,
                                                                'Värde'
                                                            ]}
                                                            labelFormatter={(label) => `Datum: ${label}`}
                                                        />
                                                        <Bar 
                                                            dataKey="value" 
                                                            fill="#8884d8"
                                                            name="Mätvärde"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {/* Results table */}
                                    <div>
                                        <h6>Alla genomföranden ({checkResults.length})</h6>
                                        <Table responsive striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Datum</th>
                                                    <th>Genomförd av</th>
                                                    {checkForStats.template.takesMeasurement && <th>Mätvärde</th>}
                                                    <th>Anteckningar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {checkResults.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>{formatDate(result.completedAt)}</td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {new Date(result.submittedAt).toLocaleDateString('sv-SE')} {new Date(result.submittedAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                            </small>
                                                        </td>
                                                        {checkForStats.template.takesMeasurement && (
                                                            <td>
                                                                {result.measurement ? (
                                                                    <div>
                                                                        <span className={result.measurement.withinRange ? 'text-success' : 'text-danger'}>
                                                                            {result.measurement.value} {result.measurement.unit}
                                                                        </span>
                                                                        {!result.measurement.withinRange && (
                                                                            <Badge bg="danger" className="ms-1 small">Utanför gränsvärde</Badge>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td>
                                                            {result.notes ? (
                                                                <small>{result.notes}</small>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatsModal(false)}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta radering</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkToDelete && (
                        <>
                            <p>Är du säker på att du vill radera kontrollen <strong>"{checkToDelete.template.name}"</strong> från <strong>"{checkToDelete.buildingName}"</strong>?</p>
                            <Alert variant="info" className="mt-3">
                                <strong>Obs:</strong> Eventuell historik kommer att finnas kvar även efter radering.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteCheck}
                        disabled={saving}
                    >
                        {saving ? 'Raderar...' : 'Radera'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AllBuildingChecksPage;
